import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import {
  LuCalendarCheck,
  LuCalendarDays,
  LuChartGantt,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuRefreshCw,
  LuUserSearch,
} from 'react-icons/lu';
import ToggleChip from '../../../components/data/ToggleChip';
import SearchSelect from '../../../components/form/SearchSelect';
import Avatar from '../../../components/ui/Avatar';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import useElementWidth from '../../../hooks/useElementWidth';
import useStoredFlag from '../../../hooks/useStoredFlag';
import { useTranslation } from '../../../i18n/LanguageContext';
import { buildProjectLegend, buildProjectSlotMap, projectSlotOf } from '../../../utils/projectColors';
import { CARD, EmptyState, HelpNote, LoadingRows } from '../../ProfilePage/components/ProfileUi';
import { CalendarHelpButton, ProjectFilterButton } from '../../ProfilePage/calendar/CalendarLegend';
import DayAgenda from '../../ProfilePage/calendar/DayAgenda';
import MonthGrid from '../../ProfilePage/calendar/MonthGrid';
import { buildMonthItems, dateKey, monthCells, normalizeDate, visitDayState, visitWindow } from '../../ProfilePage/calendar/calendarDates';
import { VISIT_STATE_TONES } from '../../ProfilePage/calendar/calendarTones';
import LegendStrip from './LegendStrip';
import WeekView from './WeekView';
import { getWeekDates, makeDayData, viewRange, visitsByRange } from './opsCalendar';

// Takvim kartının genişliği: altında ızgara noktalı, üstünde seçili gün yanda,
// hafta görünümü bu genişlikten itibaren yedi sütun
const COMPACT_BELOW = 640;
const DOCKED_FROM = 1040;
const WEEK_COLUMNS_FROM = 900;

const VIEWS = ['month', 'week', 'day', 'list'];

/**
 * Takvim kartı: çalışan seçimi, gezinme, görünüm (Ay / Hafta / Gün / Liste)
 * ve seçilen çalışanın takvimi. Profil takvimiyle aynı veri ve aynı kurallar:
 * vizit penceresi çubukları, vizit günü, tamamlandığı gün, izinler.
 */
const OperationsCalendar = ({
  employees,
  selectedEmployee,
  onSelectEmployee,
  visits,
  calendarJobs,
  leaveDayMap,
  employeeMap,
  projects,
  getProjectName,
  isLoading,
  currentDate,
  setCurrentDate,
  viewMode,
  setViewMode,
  onPrev,
  onNext,
  onToday,
  onRefresh,
  onOpenVisit,
  onOpenJob,
}) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`operations.calendar.${key}`, vars);
  const cal = (key, vars) => t(`profilePage.calendar.${key}`, vars);

  const containerRef = useRef(null);
  const width = useElementWidth(containerRef, 1200);
  const compact = width < COMPACT_BELOW;
  const docked = width >= DOCKED_FROM;

  const [selectedDate, setSelectedDate] = useState(() => normalizeDate(new Date()));
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set());
  const [showWindows, setShowWindows] = useStoredFlag('habcare.operations.showWindows', true);
  const [hideCompleted, setHideCompleted] = useStoredFlag('habcare.operations.hideCompleted', false);

  const selectedEmployeeId = selectedEmployee?.id || '';

  // Çalışan değişince proje filtresi sıfırlanır (projeler çalışana göre değişir)
  useEffect(() => {
    setHiddenProjects(new Set());
  }, [selectedEmployeeId]);

  // Yalnızca aktif çalışanların kayıtları gösterilir (eski takvimle aynı kural).
  // Eski kayıtlarda planlanan gün yoksa vizit tarihi kullanılır; vizit
  // penceresi her zaman özgün kayıtla açılır.
  const isActive = useCallback((item) => {
    const employee = employeeMap.get(item.employeeId);
    return Boolean(employee && employee.Active === 1);
  }, [employeeMap]);
  const originalById = useMemo(() => new Map(visits.map((visit) => [visit.id, visit])), [visits]);
  const calendarVisits = useMemo(
    () => visits.filter(isActive).map((visit) => (visit.firstVisitDate || !visit.date ? visit : { ...visit, firstVisitDate: visit.date })),
    [visits, isActive]
  );
  const activeJobs = useMemo(() => calendarJobs.filter(isActive), [calendarJobs, isActive]);
  const openVisit = useCallback((visit) => onOpenVisit(originalById.get(visit.id) || visit), [onOpenVisit, originalById]);

  // Proje renkleri çalışanın kendi takvimindekiyle aynı: çalışanın projelerine göre
  const projectSlotMap = useMemo(() => buildProjectSlotMap(projects.filter((project) => project.employees
    && Object.values(project.employees).some((emp) => emp.id === selectedEmployeeId))), [projects, selectedEmployeeId]);
  const projectNameById = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => map.set(project.id, getProjectName(project.id)));
    return map;
  }, [projects, getProjectName]);
  const slotOf = useCallback((projectId) => projectSlotOf(projectSlotMap, projectId), [projectSlotMap]);
  const nameOf = useCallback((projectId) => getProjectName(projectId) || '', [getProjectName]);

  const shownVisits = useMemo(
    () => calendarVisits.filter((visit) => !hiddenProjects.has(visit.projectId) && !(hideCompleted && visit.status)),
    [calendarVisits, hiddenProjects, hideCompleted]
  );
  const shownJobs = useMemo(() => (hideCompleted ? activeJobs.filter((job) => !job.status) : activeJobs), [activeJobs, hideCompleted]);

  const { from, to } = viewRange(viewMode === 'list' ? 'month' : viewMode, currentDate);
  const fromKey = dateKey(from);
  const toKey = dateKey(to);
  const dayMap = useMemo(() => visitsByRange(shownVisits, from, to),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shownVisits, fromKey, toKey]);
  const dayData = useMemo(() => makeDayData(dayMap, shownJobs, leaveDayMap), [dayMap, shownJobs, leaveDayMap]);
  const monthItems = useMemo(() => buildMonthItems({ visits: shownVisits, jobs: shownJobs, showWindows }), [shownVisits, shownJobs, showWindows]);
  const cells = useMemo(() => monthCells(currentDate), [currentDate]);
  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);

  const inShownMonth = (date) => date.getFullYear() === currentDate.getFullYear() && date.getMonth() === currentDate.getMonth();
  const inRange = (date) => date >= normalizeDate(from) && date <= normalizeDate(to);

  // Ay değişince seçili gün o aya taşınır (bugün o aydaysa bugün)
  useEffect(() => {
    if (inShownMonth(selectedDate)) return;
    const today = normalizeDate(new Date());
    setSelectedDate(inShownMonth(today) ? today : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  // Görünümün kapsadığı günlerin özeti: vizitler vizit günündeki durumlarıyla bir kez sayılır
  const summary = useMemo(() => {
    const counts = { total: 0, planned: 0, inProgress: 0, overdue: 0, completed: 0, screenFail: 0, jobs: 0 };
    shownVisits.forEach((visit) => {
      const date = normalizeDate(new Date(visit.firstVisitDate));
      if (Number.isNaN(date.getTime()) || !inRange(date)) return;
      counts.total += 1;
      if (visit.screenFail) {
        counts.screenFail += 1;
        return;
      }
      const state = visitDayState(visit, date);
      if (state === 'completedDay' || state === 'windowClosed' || state === 'completed') counts.completed += 1;
      else counts[state] += 1;
    });
    counts.jobs = shownJobs.filter((job) => {
      const start = normalizeDate(new Date(job.startDate));
      return !Number.isNaN(start.getTime()) && inRange(start);
    }).length;
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownVisits, shownJobs, fromKey, toKey]);

  // Proje filtresinde, penceresi görünen aralığa düşen projeler listelenir
  const rangeProjects = useMemo(() => {
    const ids = calendarVisits
      .filter((visit) => {
        if (!visit.firstVisitDate) return false;
        const { start, end } = visitWindow(visit);
        return !Number.isNaN(start.getTime()) && start <= normalizeDate(to) && end >= normalizeDate(from);
      })
      .map((visit) => visit.projectId);
    return buildProjectLegend(ids, projectSlotMap, projectNameById);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarVisits, fromKey, toKey, projectSlotMap, projectNameById]);

  const toggleProject = (projectId) => {
    setHiddenProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // Aydan hafta/gün görünümüne geçerken seçili gün açılır; geri dönerken o gün seçilir
  const changeView = (next) => {
    if (viewMode === 'month' && (next === 'week' || next === 'day') && inShownMonth(selectedDate)) setCurrentDate(selectedDate);
    if ((viewMode === 'week' || viewMode === 'day') && next === 'month') setSelectedDate(normalizeDate(currentDate));
    setViewMode(next);
  };

  const openDay = (date) => {
    setCurrentDate(normalizeDate(date));
    setViewMode('day');
  };

  const goToday = () => {
    onToday();
    setSelectedDate(normalizeDate(new Date()));
  };

  const shortDay = (date, withYear) => date.toLocaleDateString(locale, { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
  let navTitle = currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  if (viewMode === 'week') navTitle = `${shortDay(weekDates[0])} – ${shortDay(weekDates[6], true)}`;
  if (viewMode === 'day') navTitle = currentDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const selected = inShownMonth(selectedDate) ? selectedDate : null;
  const selectedData = selected ? dayData(selected) : { entries: [], windowVisits: [], leaves: [] };
  const listDays = viewMode === 'list'
    ? cells.filter(Boolean).map((date) => ({ date, ...dayData(date) })).filter((day) => day.entries.length > 0 || day.leaves.length > 0)
    : [];

  const agendaProps = {
    onOpenVisit: openVisit,
    onOpenJob,
    canAdd: false,
    projectSlotOf: slotOf,
    projectNameOf: nameOf,
  };

  const summaryItems = [
    { key: 'total', value: summary.total, dot: 'tw-bg-slate-400' },
    { key: 'planned', value: summary.planned, dot: VISIT_STATE_TONES.planned.dot },
    { key: 'inProgress', value: summary.inProgress, dot: VISIT_STATE_TONES.inProgress.dot },
    { key: 'overdue', value: summary.overdue, dot: VISIT_STATE_TONES.overdue.dot },
    { key: 'completed', value: summary.completed, dot: VISIT_STATE_TONES.completedDay.dot, hideZero: hideCompleted },
    { key: 'screenFail', value: summary.screenFail, dot: 'tw-bg-fuchsia-500', hideZero: true },
    { key: 'jobs', value: summary.jobs, dot: 'tw-bg-slate-400', hideZero: true },
  ].filter((item) => !(item.hideZero && item.value === 0));

  const employeeOptions = employees.map((emp) => ({ value: emp.id, label: `${emp.eName} ${emp.eSurname}` }));
  const visitCount = calendarVisits.length;
  const jobCount = activeJobs.length;

  let body = null;
  if (!selectedEmployee) {
    body = (
      <EmptyState
        icon={LuUserSearch}
        title={label('pickTitle')}
        text={label('pickText')}
      />
    );
  } else if (isLoading) {
    body = <LoadingRows count={4} height="tw-h-24" label={label('loading')} />;
  } else if (viewMode === 'week') {
    body = (
      <WeekView
        weekDates={weekDates}
        dayData={dayData}
        columns={width >= WEEK_COLUMNS_FROM}
        onOpenVisit={openVisit}
        onOpenJob={onOpenJob}
        onOpenDay={openDay}
        projectSlotOf={slotOf}
        projectNameOf={nameOf}
      />
    );
  } else if (viewMode === 'day') {
    const day = dayData(normalizeDate(currentDate));
    body = (
      <div className="tw-mx-auto tw-max-w-3xl">
        <DayAgenda date={normalizeDate(currentDate)} entries={day.entries} windowVisits={day.windowVisits} leaves={day.leaves} showNotes {...agendaProps} />
      </div>
    );
  } else if (viewMode === 'list') {
    body = listDays.length === 0 ? (
      <EmptyState icon={LuCalendarCheck} title={label('emptyMonth')} text={label('emptyMonthHint')} />
    ) : (
      <div className="tw-space-y-5">
        {listDays.map((day) => (
          <DayAgenda key={dateKey(day.date)} date={day.date} entries={day.entries} leaves={day.leaves} showEmpty={false} {...agendaProps} />
        ))}
      </div>
    );
  } else {
    body = (
      <div className={docked ? 'tw-grid tw-items-start tw-gap-4 tw-grid-cols-[minmax(0,1fr)_21rem]' : 'tw-space-y-4'}>
        <MonthGrid
          cells={cells}
          items={monthItems}
          dayData={dayData}
          selectedKey={selected ? dateKey(selected) : null}
          compact={compact}
          showWindows={showWindows}
          onSelectDay={(date) => setSelectedDate(normalizeDate(date))}
          onOpenVisit={openVisit}
          onOpenJob={onOpenJob}
          projectSlotOf={slotOf}
          projectNameOf={nameOf}
        />
        {selected && (
          <motion.div
            key={dateKey(selected)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className={docked ? 'tw-sticky tw-top-4 tw-max-h-[calc(100vh-2rem)] tw-overflow-y-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-p-3.5 glass-scrollbar dark:tw-border-white/10 dark:tw-bg-white/[0.03]' : ''}
          >
            <DayAgenda date={selected} entries={selectedData.entries} windowVisits={selectedData.windowVisits} leaves={selectedData.leaves} {...agendaProps} />
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <section ref={containerRef} aria-label={label('title')} className={`${CARD} tw-space-y-4 tw-p-3.5 sm:tw-p-5`}>
      {/* Çalışan seçimi */}
      <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center">
          <div className="tw-w-full sm:tw-w-80">
            <label htmlFor="operations-employee" className="tw-mb-1.5 tw-block tw-px-0.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
              {label('employee')}
            </label>
            <SearchSelect
              inputId="operations-employee"
              options={employeeOptions}
              value={selectedEmployeeId}
              onChange={onSelectEmployee}
              placeholder={label('employeePlaceholder')}
              noOptionsText={label('noEmployees')}
              isClearable
            />
          </div>
          {selectedEmployee && (
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 sm:tw-mt-5">
              <Avatar firstName={selectedEmployee.eName} lastName={selectedEmployee.eSurname} imageUrl={selectedEmployee.profileImageUrl} size="lg" />
              <div className="tw-min-w-0">
                <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{`${selectedEmployee.eName} ${selectedEmployee.eSurname}`}</p>
                <p className="tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {isLoading ? label('loadingShort') : label('recordCounts', { visits: visitCount, jobs: jobCount })}
                </p>
              </div>
            </div>
          )}
        </div>
        {selectedEmployee && (
          <Button size="sm" variant="ghost" onClick={onRefresh} disabled={isLoading} className="tw-self-start lg:tw-self-auto">
            <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${isLoading ? 'tw-animate-spin' : ''}`} />
            {label('refresh')}
          </Button>
        )}
      </div>

      {selectedEmployee && (
        <>
          <HelpNote>{label('sameAsProfile')}</HelpNote>

          {/* Gezinme ve görünüm */}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-slate-200/80 tw-pt-4 dark:tw-border-white/10">
            <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-1.5 sm:tw-w-auto">
              <IconButton label={label(`prev.${viewMode}`)} onClick={onPrev}>
                <LuChevronLeft aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </IconButton>
              <h2 aria-live="polite" className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-center tw-text-base tw-font-semibold tw-capitalize tw-tracking-tight tw-text-slate-900 sm:tw-min-w-[12rem] sm:tw-flex-none sm:tw-text-lg dark:tw-text-white">
                {navTitle}
              </h2>
              <IconButton label={label(`next.${viewMode}`)} onClick={onNext}>
                <LuChevronRight aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </IconButton>
              <Button size="sm" variant="ghost" onClick={goToday} title={cal('today')} className="sm:tw-ml-1">
                <LuCalendarCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
                <span className="max-sm:tw-sr-only">{cal('today')}</span>
              </Button>
            </div>
            <div className="tw-max-w-full tw-overflow-x-auto tw-pb-0.5">
              <SegmentedControl
                size="sm"
                layoutId="operations-calendar-view"
                label={label('viewLabel')}
                value={VIEWS.includes(viewMode) ? viewMode : 'month'}
                onChange={changeView}
                options={VIEWS.map((view) => ({ value: view, label: label(`views.${view}`) }))}
              />
            </div>
          </div>

          {/* Özet ve görünüm seçenekleri */}
          <div className="tw-flex tw-flex-col tw-gap-2.5 2xl:tw-flex-row 2xl:tw-items-center 2xl:tw-justify-between">
            <ul aria-label={label('summaryLabel')} className="tw-flex tw-min-w-0 tw-gap-2 tw-overflow-x-auto tw-pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
              {summaryItems.map((item) => (
                <li key={item.key} className="tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-1.5 tw-text-xs dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
                  <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-rounded-full ${item.dot}`} />
                  <span className="tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{isLoading ? '…' : item.value}</span>
                  <span className="tw-text-slate-500 dark:tw-text-slate-400">{cal(`summary.${item.key}`)}</span>
                </li>
              ))}
            </ul>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 2xl:tw-shrink-0">
              {viewMode === 'month' && (
                <ToggleChip pressed={showWindows} onChange={setShowWindows} icon={LuChartGantt} label={cal('options.windows')} title={cal('options.windowsHint')} />
              )}
              <ToggleChip pressed={hideCompleted} onChange={setHideCompleted} icon={LuCircleCheck} label={cal('options.hideCompleted')} title={cal('options.hideCompletedHint')} tone="amber" />
              <ProjectFilterButton projects={rangeProjects} hiddenProjects={hiddenProjects} onToggleProject={toggleProject} onShowAll={() => setHiddenProjects(new Set())} />
              <CalendarHelpButton />
            </div>
          </div>
        </>
      )}

      {body}

      {selectedEmployee && !isLoading && <LegendStrip />}
      {!selectedEmployee && (
        <p className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">
          <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {label('lazyNote')}
        </p>
      )}
    </section>
  );
};

OperationsCalendar.propTypes = {
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedEmployee: PropTypes.object,
  onSelectEmployee: PropTypes.func.isRequired,
  visits: PropTypes.arrayOf(PropTypes.object).isRequired,
  calendarJobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  leaveDayMap: PropTypes.instanceOf(Map).isRequired,
  employeeMap: PropTypes.instanceOf(Map).isRequired,
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  getProjectName: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  setCurrentDate: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  setViewMode: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onToday: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onOpenVisit: PropTypes.func.isRequired,
  onOpenJob: PropTypes.func.isRequired,
};

export default OperationsCalendar;
